import { useEffect, useMemo } from 'react'
import { AlertTriangle, RefreshCw } from 'lucide-react'
import { isRouteErrorResponse, useRouteError } from 'react-router-dom'

import { Button, Panel } from '@/components/ui'

const AUTO_RELOAD_MARKER = 'crm-spa-auto-reload'

function getErrorMessage(error: unknown) {
  if (isRouteErrorResponse(error)) {
    return error.data?.message || error.statusText || 'Не удалось открыть экран.'
  }

  if (error instanceof Error) {
    return error.message || 'Не удалось открыть экран.'
  }

  return 'Не удалось открыть экран.'
}

function shouldReload(message: string) {
  return [
    'Failed to fetch dynamically imported module',
    'Importing a module script failed',
    'Class extends value',
  ].some((fragment) => message.includes(fragment))
}

export function RouteErrorPage() {
  const error = useRouteError()
  const message = useMemo(() => getErrorMessage(error), [error])

  useEffect(() => {
    if (!shouldReload(message) || typeof window === 'undefined') {
      return
    }

    const marker = `${AUTO_RELOAD_MARKER}:${window.location.pathname}`
    if (window.sessionStorage.getItem(marker)) {
      return
    }

    window.sessionStorage.setItem(marker, '1')
    window.location.reload()
  }, [message])

  return (
    <div className="flex min-h-screen items-center justify-center px-4 py-10">
      <Panel className="w-full max-w-2xl space-y-6 p-8 lg:p-10">
        <div className="flex items-start gap-4">
          <div className="rounded-3xl bg-amber-50 p-4 text-amber-700">
            <AlertTriangle className="h-6 w-6" />
          </div>
          <div className="space-y-2">
            <p className="text-xs font-semibold uppercase tracking-[0.24em] text-amber-700">
              Внимание
            </p>
            <h1 className="text-3xl font-bold text-ink-950">Экран не удалось открыть</h1>
            <p className="text-sm text-ink-500">
              Обновите страницу и повторите действие. Если проблема останется, попробуйте зайти
              снова через несколько секунд.
            </p>
          </div>
        </div>

        <div className="rounded-[24px] border border-ink-100 bg-slate-50 p-5 text-sm text-ink-600">
          {message}
        </div>

        <div className="flex flex-wrap gap-3">
          <Button onClick={() => window.location.reload()}>
            <RefreshCw className="mr-2 h-4 w-4" />
            Обновить страницу
          </Button>
          <Button onClick={() => window.location.assign('/')} tone="ghost">
            На рабочий стол
          </Button>
        </div>
      </Panel>
    </div>
  )
}
